// src/contexts/ThemeContext.js
//
// Sacred Canvas / Digital Medersa theme.
// All tokens come from ../theme/sacredCanvas.js

import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { createTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { palette, fonts, radii, focus, motion } from '../theme/sacredCanvas';

// MUI typography can't read CSS variables directly, but we can make
// every Typography variant point to the same var. The FontContext sets
// the var on <html>; anything consuming var(--sacred-font-*) updates
// instantly when the user flips fonts.
const FONT_DISPLAY = 'var(--sacred-font-display, ' + fonts.display + ')';
const FONT_BODY    = 'var(--sacred-font-body, '    + fonts.body    + ')';
const FONT_LABEL   = 'var(--sacred-font-label, '   + fonts.label   + ')';

const ThemeContext = createContext();

export const useThemeContext = () => useContext(ThemeContext);

export const AppThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(localStorage.getItem('themeMode') || 'light');
  const prefersReducedMotion = useMediaQuery(
    '(prefers-reduced-motion: reduce)',
    { noSsr: true },
  );

  useEffect(() => {
    localStorage.setItem('themeMode', mode);
    document.documentElement.dataset.theme = mode;
  }, [mode]);

  const colorMode = useMemo(
    () => ({
      mode,
      toggleColorMode: () => {
        setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
      },
    }),
    [mode],
  );

  const theme = useMemo(() => {
    const c = mode === 'light' ? palette.light : palette.dark;
    const f = mode === 'light' ? focus.light : focus.dark;
    // Reduced-motion users get instant transitions; everyone else
    // uses the Sacred Canvas motion ladder. MUI threads
    // transitions.duration through every internal transition
    // (Dialog, Tooltip, Drawer, Slide, Fade, Grow, Zoom, Collapse).
    const dur = (d) => (prefersReducedMotion ? 0 : d);
    return createTheme({
      direction: 'rtl',
      shape: { borderRadius: radii.xl },
      transitions: {
        duration: {
          shortest:        dur(motion.duration.instant),
          shorter:         dur(motion.duration.quick),
          short:           dur(motion.duration.calm),
          standard:        dur(motion.duration.calm),
          complex:         dur(motion.duration.deliberate),
          enteringScreen:  dur(motion.duration.calm),
          leavingScreen:   dur(motion.duration.quick),
        },
        easing: {
          easeInOut: motion.easing.outQuart,
          easeOut:   motion.easing.outQuart,
          easeIn:    motion.easing.outExpo,
          sharp:     motion.easing.outQuint,
        },
      },
      palette: {
        mode,
        primary: { main: c.primary, dark: c.primaryContainer, light: c.primaryFixed, contrastText: c.onPrimary },
        secondary: { main: c.secondary, dark: c.secondaryContainer, light: c.secondaryFixed, contrastText: c.onSecondary },
        info: { main: c.tertiary, dark: c.tertiaryContainer, light: c.tertiaryFixed, contrastText: c.onTertiary },
        error: { main: c.error, contrastText: c.onError },
        success: { main: c.success },
        warning: { main: c.warning },
        background: { default: c.surface, paper: c.surfaceContainerLowest },
        text: { primary: c.onSurface, secondary: c.onSurfaceVariant },
        divider: c.outlineVariant,
        sacred: c, // full token set accessible via theme.palette.sacred.*
      },
      typography: {
        fontFamily: FONT_BODY,
        fontSize: 16,
        htmlFontSize: 16,
        fontWeightLight: 400,
        fontWeightRegular: 400,
        fontWeightMedium: 600,
        fontWeightBold: 700,
        // Fluid headings — grow with viewport width between the min/max
        // caps. Using px-in-clamp (rather than rem) decouples this from
        // the root-font clamp so headings don't double-scale on 4K.
        h1: { fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 'clamp(2rem, 3.2vw + 0.5rem, 3.5rem)',  lineHeight: 1.15 },
        h2: { fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 'clamp(1.65rem, 2.2vw + 0.5rem, 2.75rem)', lineHeight: 1.2 },
        h3: { fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 'clamp(1.4rem, 1.4vw + 0.6rem, 2rem)',   lineHeight: 1.25 },
        h4: { fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 'clamp(1.2rem, 0.9vw + 0.7rem, 1.625rem)', lineHeight: 1.3 },
        h5: { fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 'clamp(1.05rem, 0.5vw + 0.75rem, 1.375rem)', lineHeight: 1.35 },
        h6: { fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 'clamp(0.95rem, 0.25vw + 0.75rem, 1.15rem)', lineHeight: 1.4 },
        subtitle1: { fontFamily: FONT_BODY, fontWeight: 600, fontSize: '1.125rem', lineHeight: 1.6 },
        subtitle2: { fontFamily: FONT_BODY, fontWeight: 600, fontSize: '1rem', lineHeight: 1.55 },
        body1: { fontFamily: FONT_BODY, fontWeight: 400, fontSize: '1.0625rem', lineHeight: 1.8 },
        body2: { fontFamily: FONT_BODY, fontWeight: 400, fontSize: '0.9375rem', lineHeight: 1.7 },
        button: { fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: '1rem', textTransform: 'none', letterSpacing: 0 },
        caption: { fontFamily: FONT_BODY, fontWeight: 400, fontSize: '0.75rem', letterSpacing: 0 },
        overline: { fontFamily: FONT_BODY, fontWeight: 500, fontSize: '0.7rem', letterSpacing: 0 },
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            // Root font-size is set responsively in src/index.css so it
            // scales up on high-DPI / 4K displays. Don't override it here.
            body: {
              fontFamily: FONT_BODY,
              fontWeight: 400,
              backgroundColor: c.surface,
              color: c.onSurface,
              // Neutral-surface paint for every <BayanIcon> (see BayanIcon.jsx,
              // which masks the glyph and fills it with this variable): brand
              // green on the light theme, white on the dark theme — always the
              // max-contrast choice. Filled/dark containers below flip it to
              // `currentColor` so the icon takes the container's contrast text
              // colour (white on a green button, black on a gold chip) instead
              // of vanishing.
              '--bayan-icon-ink': mode === 'light' ? c.primary : '#FFFFFF',
            },
            // When a BayanIcon sits inside a filled (contained / selected /
            // filled-chip / FAB) surface — e.g. the green "حاضر" attendance
            // toggle — inherit that surface's text colour so the glyph reads
            // as a lighter shade against the dark-green fill.
            '.MuiButton-contained, .MuiToggleButton-root.Mui-selected, .MuiChip-filled, .MuiFab-root, .MuiAvatar-root, [data-on-fill="true"]': {
              '--bayan-icon-ink': 'currentColor',
            },
            // Theme-level font inheritance (replaces the !important rules
            // that used to live in index.css). Every Arabic-script
            // surface inherits FONT_BODY; headings + display-class +
            // MUI Typography heading variants inherit FONT_DISPLAY. Icon
            // fonts and monospace are exempt by selector.
            'body *:where(:not(.material-symbols-outlined):not(.material-icons):not(.material-icons-outlined):not(.material-icons-round):not(.material-icons-sharp):not(.material-icons-two-tone):not([class*="MuiSvgIcon"]):not(code):not(pre):not(kbd):not(samp):not(.sacred-font-fixed))': {
              fontFamily: FONT_BODY,
            },
            'body h1, body h2, body h3, body h4, body h5, body h6, body .sacred-display, body .MuiTypography-h1, body .MuiTypography-h2, body .MuiTypography-h3, body .MuiTypography-h4, body .MuiTypography-h5, body .MuiTypography-h6': {
              fontFamily: FONT_DISPLAY,
            },
            // S4 — brand-correct focus ring on every interactive surface
            // not already styled by a MUI component override below
            // (anchors, the Lawh-as-link utility class, summary/details).
            // Mouse clicks don't trigger this; only keyboard focus.
            'a:focus-visible, .lawh-link:focus-visible, summary:focus-visible': {
              outline: f.outline,
              outlineOffset: 2,
              boxShadow: f.boxShadow,
              borderRadius: `${radii.md}px`,
            },
            '*::selection': { backgroundColor: c.secondaryFixed, color: c.onSecondary },
            // S8 — Sacred Canvas motion: route cross-fade. Wrap the
            // <Routes> tree with .route-mount keyed on pathname so
            // each navigation re-mounts and replays the animation.
            // No layout-property animation; opacity only.
            '@keyframes sacredRouteFadeIn': {
              from: { opacity: 0 },
              to:   { opacity: 1 },
            },
            '.route-mount': prefersReducedMotion
              ? { animation: 'none' }
              : {
                  animation: `sacredRouteFadeIn ${motion.duration.quick}ms ${motion.easing.outQuart} both`,
                },
          },
        },
        MuiContainer: {
          styleOverrides: {
            // "Fill the screen" density target — let dense dashboard / table
            // pages use most of a 1080p / 1440p / 4K viewport instead of
            // sitting in a 1600px column with empty side gutters. The vw
            // term keeps a slim breathing gutter on ultra-wide so content
            // never goes truly edge-to-edge; the px ceiling stops a single
            // page from sprawling past a sane maximum on native 4K. Narrow
            // reading pages opt out by setting maxWidth="md"/"sm".
            maxWidthLg: { maxWidth: 'min(2080px, 93vw) !important' },
            maxWidthXl: { maxWidth: 'min(2560px, 95vw) !important' },
          },
        },
        MuiPaper: {
          defaultProps: { elevation: 0 },
          styleOverrides: {
            root: {
              backgroundImage: 'none',
              backgroundColor: c.surfaceContainerLow,
              borderRadius: radii.xl,
            },
          },
        },
        MuiCard: {
          defaultProps: { elevation: 0 },
          styleOverrides: {
            root: {
              borderRadius: radii.xl,
              backgroundColor: c.surfaceContainerHighest,
              backgroundImage: 'none',
              border: 'none',
            },
          },
        },
        MuiButton: {
          defaultProps: { disableElevation: true },
          styleOverrides: {
            root: {
              borderRadius: radii.md,
              paddingInline: 20,
              paddingBlock: 10,
              fontWeight: 700,
              // Arabic body font carries a tall intrinsic line-box; the MUI
              // default button lineHeight (1.75) leaves the glyphs sitting
              // low so the flex-centred start/end icon floats above them.
              // Pin a tight lineHeight and force the icon to centre on the
              // text so icon + label share one optical baseline.
              lineHeight: 1.4,
              '& .MuiButton-startIcon, & .MuiButton-endIcon': {
                alignSelf: 'center',
                marginTop: 0,
                marginBottom: 0,
                // Arabic glyphs sit below the geometric line centre, so a
                // box-centred icon reads as floating high next to them. A
                // 1px optical nudge settles the icon onto the Arabic
                // text's visual middle.
                transform: 'translateY(1px)',
              },
              // RTL margin fix: the app runs `direction: 'rtl'` without the
              // emotion stylis-rtl plugin, so MUI's logical start/end-icon
              // margins (marginRight:8 / marginLeft:-4) never flip. In an
              // RTL row the startIcon renders on the RIGHT of the label, so
              // the -4 lands between icon and text and jams the glyph into
              // the words. Pin physically-correct margins: gap toward the
              // label, slight pull toward the button edge.
              '& .MuiButton-startIcon': {
                marginLeft: 8,
                marginRight: -4,
              },
              '& .MuiButton-endIcon': {
                marginRight: 8,
                marginLeft: -4,
              },
              transition: prefersReducedMotion
                ? 'none'
                : `transform ${motion.duration.calm}ms ${motion.easing.outQuart}, box-shadow ${motion.duration.calm}ms ${motion.easing.outQuart}, background-color ${motion.duration.calm}ms ${motion.easing.outQuart}`,
              '&:hover': prefersReducedMotion
                ? { boxShadow: `0 12px 32px -16px ${c.primary}55` }
                : { transform: 'translateY(-1px)', boxShadow: `0 12px 32px -16px ${c.primary}55` },
              '&:focus-visible': {
                outline: f.outline,
                outlineOffset: 2,
                boxShadow: f.boxShadow,
              },
              '&.Mui-disabled': {
                opacity: 0.5,
                color: 'inherit',
                backgroundColor: 'inherit',
              },
            },
            containedPrimary: {
              backgroundColor: c.primary,
              color: c.onPrimary,
              '&:hover': { backgroundColor: c.primaryContainer },
              '&.Mui-disabled': { backgroundColor: c.primary, color: c.onPrimary },
            },
            containedSecondary: {
              backgroundColor: c.secondary,
              color: c.onSecondary,
              '&:hover': { backgroundColor: c.secondaryContainer },
              '&.Mui-disabled': { backgroundColor: c.secondary, color: c.onSecondary },
            },
            outlined: {
              borderColor: c.outlineVariant,
              color: c.onSurface,
              '&.Mui-disabled': { borderColor: c.outlineVariant, color: c.onSurface },
            },
          },
        },
        MuiIconButton: {
          styleOverrides: {
            root: {
              borderRadius: radii.md,
              '&:focus-visible': {
                outline: f.outline,
                outlineOffset: 2,
                boxShadow: f.boxShadow,
              },
            },
          },
        },
        MuiToggleButtonGroup: {
          styleOverrides: {
            root: {
              '& .MuiToggleButtonGroup-firstButton': {
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                borderTopRightRadius: radii.md,
                borderBottomRightRadius: radii.md,
              },
              '& .MuiToggleButtonGroup-lastButton': {
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                borderTopLeftRadius: radii.md,
                borderBottomLeftRadius: radii.md,
                marginLeft: 0,
                marginRight: -1,
                borderLeft: 'inherit',
              },
              '& .MuiToggleButtonGroup-middleButton': {
                marginLeft: 0,
                marginRight: -1,
              },
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: {
              borderRadius: radii.full,
              fontFamily: FONT_BODY,
              fontWeight: 600,
              paddingInline: 4,
            },
            // The Arabic body font renders low inside the chip's fixed
            // height; centre the label box and tighten its line-height so
            // the text sits on the chip's optical middle (and any chip icon
            // lines up with it).
            label: {
              lineHeight: 1,
              display: 'inline-flex',
              alignItems: 'center',
            },
            // RTL margin fix (same class of bug as MuiButton above): the app
            // runs `direction: 'rtl'` WITHOUT the emotion stylis-rtl plugin,
            // so MUI's logical icon margins (marginLeft:5 / marginRight:-6)
            // stay physical and never flip. In an RTL chip the icon renders
            // on the RIGHT, so the unflipped -6 lands between icon and label
            // and jams the glyph off-centre. Swap to the physically-correct
            // pair: gap toward the label (left), slight pull toward the chip
            // edge (right). Also normalise the icon to the chip's vertical
            // centre so the glyph doesn't float high next to Arabic text.
            icon: {
              marginRight: 5,
              marginLeft: -6,
              alignSelf: 'center',
            },
            iconSmall: {
              marginRight: 4,
              marginLeft: -4,
              alignSelf: 'center',
            },
            deleteIcon: {
              marginLeft: 5,
              marginRight: -6,
            },
            deleteIconSmall: {
              marginLeft: 4,
              marginRight: -4,
            },
          },
        },
        MuiTextField: {
          defaultProps: { variant: 'filled' },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: {
              left: 'auto',
              right: 0,
              transformOrigin: 'top right',
            },
            filled: {
              transform: 'translate(-12px, 16px) scale(1)',
              '&.MuiInputLabel-shrink': {
                transform: 'translate(-12px, 7px) scale(0.75)',
              },
            },
            outlined: {
              transform: 'translate(-14px, 16px) scale(1)',
              '&.MuiInputLabel-shrink': {
                transform: 'translate(-14px, -9px) scale(0.75)',
              },
            },
          },
        },
        MuiFormLabel: {
          styleOverrides: {
            root: { textAlign: 'right' },
          },
        },
        MuiFilledInput: {
          styleOverrides: {
            root: {
              backgroundColor: c.surfaceContainerLow,
              borderRadius: `${radii.md}px ${radii.md}px 0 0`,
              '&:hover': { backgroundColor: c.surfaceContainer },
              '&.Mui-focused': { backgroundColor: c.surfaceContainer },
              '&:before': { borderBottomColor: c.outlineVariant },
              '&:after': { borderBottomColor: c.secondary },
              '&:focus-within': {
                outline: f.outline,
                outlineOffset: 2,
                boxShadow: f.boxShadow,
              },
            },
            input: { textAlign: 'right' },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              borderRadius: radii.md,
              backgroundColor: c.surfaceContainerLow,
              '& fieldset': { borderColor: c.outlineVariant, textAlign: 'right' },
              '&:hover fieldset': { borderColor: c.outline },
              '&.Mui-focused fieldset': { borderColor: c.secondary, borderWidth: 2 },
              '& legend': { textAlign: 'right', marginRight: 8, marginLeft: 0 },
              '&:focus-within': {
                outline: f.outline,
                outlineOffset: 2,
                boxShadow: f.boxShadow,
              },
            },
            input: { textAlign: 'right' },
            notchedOutline: { textAlign: 'right' },
          },
        },
        MuiSelect: {
          styleOverrides: {
            // Arabic-first UI — flip the dropdown chevron to the visual end
            // of the field (left side in RTL).
            icon: { right: 'auto', left: 7 },
            select: { textAlign: 'right', paddingLeft: 32, paddingRight: 12 },
          },
        },
        MuiNativeSelect: {
          styleOverrides: {
            icon: { right: 'auto', left: 7 },
          },
        },
        MuiAutocomplete: {
          styleOverrides: {
            endAdornment: { right: 'auto', left: 9 },
            inputRoot: { paddingLeft: 56, paddingRight: 14 },
          },
        },
        MuiFormHelperText: {
          styleOverrides: {
            root: { textAlign: 'right' },
          },
        },
        MuiDivider: {
          styleOverrides: { root: { borderColor: c.outlineVariant, opacity: 0.6 } },
        },
        MuiAppBar: {
          defaultProps: { elevation: 0, color: 'transparent' },
          styleOverrides: {
            root: {
              backgroundColor: `${c.surfaceContainerLow}D9`,
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              borderBottom: `1px solid ${c.outlineVariant}33`,
              color: c.onSurface,
            },
          },
        },
        MuiDrawer: {
          styleOverrides: {
            paper: {
              backgroundColor: c.surfaceContainerLow,
              borderLeft: `1px solid ${c.outlineVariant}33`,
              borderRight: `1px solid ${c.outlineVariant}33`,
              backgroundImage: 'none',
            },
          },
        },
        MuiTooltip: {
          defaultProps: {
            enterDelay: 200,
            enterNextDelay: 0,
            leaveDelay: 0,
            disableInteractive: true,
          },
          styleOverrides: {
            tooltip: {
              backgroundColor: c.ink,
              color: c.surface,
              fontFamily: FONT_BODY,
              fontSize: '0.8rem',
              borderRadius: radii.md,
              paddingInline: 10,
              pointerEvents: 'none',
            },
            popper: {
              pointerEvents: 'none',
            },
          },
        },
        MuiListItemButton: {
          styleOverrides: {
            root: {
              borderRadius: radii.md,
              margin: '4px 8px',
              '&.Mui-selected': {
                backgroundColor: `${c.primary}14`,
                color: c.primary,
                borderRight: `3px solid ${c.secondary}`,
                '& .MuiListItemIcon-root': { color: c.primary },
                '& .MuiListItemText-primary': { color: c.primary, fontWeight: 700 },
              },
              '&:focus-visible': {
                outline: f.outline,
                outlineOffset: -2,
                boxShadow: f.boxShadow,
              },
            },
          },
        },
        MuiLink: {
          styleOverrides: {
            root: {
              '&:focus-visible': {
                outline: f.outline,
                outlineOffset: 2,
                boxShadow: f.boxShadow,
                borderRadius: `${radii.md}px`,
              },
            },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: {
              '&:focus-visible': {
                outline: f.outline,
                outlineOffset: -2,
                boxShadow: f.boxShadow,
              },
            },
          },
        },
        MuiCheckbox: {
          styleOverrides: {
            root: {
              '&:focus-visible': {
                outline: f.outline,
                outlineOffset: -2,
                boxShadow: f.boxShadow,
                borderRadius: `${radii.md}px`,
              },
            },
          },
        },
        MuiSwitch: {
          styleOverrides: {
            switchBase: {
              '&:focus-visible': {
                outline: f.outline,
                outlineOffset: -4,
                boxShadow: f.boxShadow,
              },
            },
          },
        },
        MuiToggleButton: {
          styleOverrides: {
            root: {
              '&:focus-visible': {
                outline: f.outline,
                outlineOffset: 2,
                boxShadow: f.boxShadow,
              },
            },
          },
        },
        MuiLinearProgress: {
          styleOverrides: {
            root: { borderRadius: radii.full, height: 6, backgroundColor: `${c.primary}1A` },
            bar: { borderRadius: radii.full, backgroundColor: c.primary },
          },
        },
        MuiDialog: {
          styleOverrides: {
            // On phones, shrink the default 32px dialog gutter to 12px so
            // forms/editors use nearly the whole viewport (no wasted side
            // voids) while keeping rounded corners. fullScreen dialogs
            // (set per-instance) override this and go edge-to-edge.
            paper: {
              borderRadius: radii.xl,
              backgroundColor: c.surfaceContainerLow,
              backgroundImage: 'none',
              '@media (max-width:600px)': {
                margin: 12,
                width: 'calc(100% - 24px)',
                maxWidth: 'calc(100% - 24px)',
                maxHeight: 'calc(100% - 24px)',
              },
            },
          },
        },
        // S10 — every save/error toast becomes a live region. SnackbarContent
        // is the default child of <Snackbar message=…> so this propagates
        // without the call sites having to set props per-instance. Errors
        // routed through Alerts retain MUI's role="alert" automatically.
        MuiSnackbarContent: {
          defaultProps: {
            role: 'status',
            'aria-live': 'polite',
            'aria-atomic': 'true',
          },
        },
        MuiTableCell: {
          styleOverrides: {
            root: { borderColor: `${c.outlineVariant}33`, fontFamily: FONT_BODY },
            head: {
              fontFamily: FONT_BODY,
              letterSpacing: 0,
              fontSize: '0.75rem',
              color: c.outline,
              backgroundColor: `${c.primary}08`,
              fontWeight: 600,
            },
          },
        },
      },
    });
  }, [mode, prefersReducedMotion]);

  return (
    <ThemeContext.Provider value={colorMode}>
      {children(theme)}
    </ThemeContext.Provider>
  );
};
